import { supabase } from '@/lib/supabase';
const { safeLog } = require('../utils/safeLog.js');

export interface PatientInvitationData {
  email: string;
  full_name: string;
  clinic_id: string;
  invitation_token?: string;
}

export interface MagicLinkAuthResult {
  success: boolean;
  user?: any;
  error?: string;
}

export class MagicLinkService {
  // Service is in standby mode - magic link functionality DISABLED (using OTP instead)
  static readonly STANDBY_MODE = true;

  /**
   * Send first-access magic link for password setup (DUAL-PATH AUTHENTICATION)  
   * Supports both web panel created users and direct mobile users
   * Profile creation/linking happens AFTER auth user creation in magic link handler
   */
  static async sendFirstAccessLink(email: string): Promise<{ success: boolean; message?: string; error?: any }> {
    try {
      const trimmedEmail = email.trim().toLowerCase();
      safeLog.info('🔗 Sending first-access magic link for:', trimmedEmail);

      // Send magic link (creates Supabase Auth user)
      // Profile linking happens in magic-link-login handler after auth user exists
      const { error } = await supabase.auth.signInWithOtp({
        email: trimmedEmail,
        options: {
          shouldCreateUser: true, // Creates auth user for both existing/new profiles
          emailRedirectTo: 'https://mobile.padrinho.app/api/magic-link-redirect'
        },
      });

      if (error) {
        safeLog.error('❌ Error sending first-access magic link:', error);
        return { 
          success: false, 
          message: 'Erro ao enviar link de acesso',
          error 
        };
      }

      safeLog.info('✅ First-access magic link sent successfully');
      return { 
        success: true, 
        message: 'Link de acesso enviado! Verifique sua caixa de entrada para continuar.' 
      };
      
    } catch (error) {
      safeLog.error('❌ Exception sending first-access magic link:', error);
      return { 
        success: false, 
        message: 'Erro inesperado ao enviar link de acesso',
        error 
      };
    }
  }

  /**
   * Handle profile creation/linking after magic link authentication (DUAL-PATH)
   * Called from magic-link-login.tsx after auth user is created
   */
  static async handleProfileLinking(authUser: any): Promise<{ success: boolean; profile?: any; error?: any }> {
    try {
      safeLog.info('🔗 Handling profile linking for auth user:', authUser.email);

      // Check if profile exists by email (from web panel)
      const existingProfile = await this.findExistingProfileByEmail(authUser.email);
      
      if (existingProfile.found) {
        // PATH 1: Web panel user - link auth user to existing profile
        safeLog.info('🏥 Linking auth user to existing web panel profile, clinic:', existingProfile.profile?.clinic_id);
        return await this.linkAuthUserToExistingProfile(authUser, existingProfile.profile);
      } else {
        // PATH 2: Direct mobile user - create new profile with admin clinic
        safeLog.info('📱 Creating new profile for direct mobile user with admin clinic');
        return await this.createProfileForNewUser(authUser);
      }
      
    } catch (error) {
      safeLog.error('❌ Exception handling profile linking:', error);
      return { success: false, error };
    }
  }

  /**
   * Find existing profile by email (created by web panel)
   */
  static async findExistingProfileByEmail(email: string): Promise<{ found: boolean; profile?: any }> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, clinic_id, is_patient, invitation_source, patient_status')
        .eq('email', email.toLowerCase())
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        safeLog.error('❌ Error checking existing profile:', error);
        return { found: false };
      }

      if (data) {
        safeLog.info('✅ Found existing profile:', { 
          id: data.id, 
          clinic_id: data.clinic_id, 
          is_patient: data.is_patient,
          invitation_source: data.invitation_source 
        });
        return { found: true, profile: data };
      }

      return { found: false };
      
    } catch (error) {
      safeLog.error('❌ Exception checking existing profile:', error);
      return { found: false };
    }
  }

  /**
   * Link auth user to existing profile (for web panel users)
   */
  static async linkAuthUserToExistingProfile(authUser: any, existingProfile: any): Promise<{ success: boolean; profile?: any; error?: any }> {
    try {
      // Update existing profile to use auth user UUID
      const { data, error } = await supabase
        .from('profiles')
        .update({
          id: authUser.id, // Link auth user ID to profile
          invitation_accepted_at: new Date().toISOString(),
          patient_status: 'active'
        })
        .eq('email', authUser.email)
        .select()
        .single();

      if (error) {
        safeLog.error('❌ Error linking auth user to existing profile:', error);
        return { success: false, error };
      }

      // Track mobile activation for web panel
      await this.trackMobileActivation(authUser.id, authUser.email, existingProfile.clinic_id);

      safeLog.info('✅ Successfully linked auth user to existing profile');
      return { success: true, profile: data };
      
    } catch (error) {
      safeLog.error('❌ Exception linking auth user to profile:', error);
      return { success: false, error };
    }
  }

  /**
   * Create new profile for direct mobile users (assigns to admin clinic)
   */
  static async createProfileForNewUser(authUser: any): Promise<{ success: boolean; profile?: any; error?: any }> {
    try {
      const newProfile = {
        id: authUser.id, // Use auth user UUID as profile ID
        email: authUser.email,
        clinic_id: 1, // Default to admin clinic (Sponsor Team)
        is_patient: true,
        invitation_source: 'mobile_app_direct',
        invited_at: new Date().toISOString(),
        invitation_accepted_at: new Date().toISOString(),
        patient_status: 'active',
        created_by: 1, // Created by clinic 1 (admin clinic)
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('profiles')
        .insert(newProfile)
        .select()
        .single();

      if (error) {
        safeLog.error('❌ Error creating profile for new user:', error);
        return { success: false, error };
      }

      // Track mobile activation for web panel
      await this.trackMobileActivation(authUser.id, authUser.email, 1);

      safeLog.info('✅ Created new profile for direct mobile user:', { id: data.id, clinic_id: data.clinic_id });
      return { success: true, profile: data };
      
    } catch (error) {
      safeLog.error('❌ Exception creating profile for new user:', error);
      return { success: false, error };
    }
  }

  /**
   * Track mobile activation for web panel integration
   */
  static async trackMobileActivation(userId: string, email: string, clinicId: number): Promise<void> {
    try {
      safeLog.info('📱 Tracking mobile activation for web panel:', { userId, email, clinicId });
      
      // For now, just log - web panel team will review integration approach
      // In future: could insert into mobile_activations table or send webhook
      
      safeLog.info('✅ Mobile activation tracked successfully');
      
    } catch (error) {
      safeLog.warn('⚠️  Non-blocking: Failed to track mobile activation:', error);
    }
  }

  /**
   * Send patient invitation via magic link (called from Management Panel)
   * Note: STANDBY MODE - This function is currently disabled
   * Note: For web panel integration, use supabase.auth.admin.inviteUserByEmail() directly
   */
  static async sendPatientInvitation(patientData: PatientInvitationData): Promise<{ error: any }> {
    if (this.STANDBY_MODE) {
      safeLog.info('🚫 MagicLinkService: Service is in standby mode - magic link sending disabled');
      return { error: new Error('Magic link service is currently in standby mode') };
    }
    try {
      safeLog.info('📧 Sending patient invitation to:', patientData.email);

      // For mobile app usage - use OTP
      const { error } = await supabase.auth.signInWithOtp({
        email: patientData.email.trim().toLowerCase(),
        options: {
          shouldCreateUser: true,
          data: {
            full_name: patientData.full_name,
            clinic_id: patientData.clinic_id,
            invitation_source: 'management_panel',
            is_patient: true,
            invitation_token: patientData.invitation_token,
          },
          // Updated to use the production app domain for email redirect
          emailRedirectTo: 'https://mobile.padrinho.app/api/magic-link-redirect',
        },
      });

      if (error) {
        safeLog.error('❌ Error sending magic link:', error);
        return { error };
      }

      safeLog.info('✅ Magic link sent successfully');
      return { error: null };
    } catch (error) {
      safeLog.error('❌ Exception sending magic link:', error);
      return { error };
    }
  }

  /**
   * Admin function for web panel to send invitations directly
   * Note: STANDBY MODE - This function is currently disabled
   * Use this from your web panel with service role key
   */
  static async sendPatientInvitationAdmin(patientData: PatientInvitationData): Promise<{ error: any }> {
    if (this.STANDBY_MODE) {
      safeLog.info('🚫 MagicLinkService: Admin service is in standby mode - magic link sending disabled');
      return { error: new Error('Magic link admin service is currently in standby mode') };
    }
    try {
      safeLog.info('📧 [ADMIN] Sending patient invitation to:', patientData.email);

      // This requires service role key - use from web panel only
      const { error } = await supabase.auth.admin.inviteUserByEmail(
        patientData.email.trim().toLowerCase(),
        {
          data: {
            full_name: patientData.full_name,
            clinic_id: patientData.clinic_id,
            invitation_source: 'management_panel',
            is_patient: true,
            invitation_token: patientData.invitation_token || `invite_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          },
          // Updated to use the production app domain for the redirect
          redirectTo: 'https://mobile.padrinho.app/api/magic-link-redirect'
        }
      );

      if (error) {
        safeLog.error('❌ Error sending admin invitation:', error);
        return { error };
      }

      safeLog.info('✅ Admin invitation sent successfully');
      return { error: null };
    } catch (error) {
      safeLog.error('❌ Exception sending admin invitation:', error);
      return { error };
    }
  }

  /**
   * Handle magic link authentication when user clicks the link
   * Note: STANDBY MODE - This function is currently disabled
   */
  static async handleMagicLinkAuth(url: string): Promise<MagicLinkAuthResult> {
    if (this.STANDBY_MODE) {
      safeLog.info('🚫 MagicLinkService: Magic link authentication is in standby mode');
      return {
        success: false,
        error: 'Magic link authentication is currently disabled - please use the first-access flow instead'
      };
    }
    try {
      safeLog.info('🔗 Processing magic link:', url);

      // Extract token and type from URL
      const urlParams = new URLSearchParams(url.split('?')[1]);
      const token = urlParams.get('token');
      const type = urlParams.get('type');

      if (!token || type !== 'magiclink') {
        return {
          success: false,
          error: 'Invalid magic link format'
        };
      }

      // Verify the token with Supabase
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'magiclink'
      });

      if (error) {
        safeLog.error('❌ Magic link verification failed:', error);
        return {
          success: false,
          error: error.message
        };
      }

      if (data.user) {
        safeLog.info('✅ Magic link authentication successful for:', data.user.email);

        // Mark invitation as accepted
        await this.markInvitationAccepted(data.user.id);

        return {
          success: true,
          user: data.user
        };
      }

      return {
        success: false,
        error: 'No user data returned'
      };
    } catch (error) {
      safeLog.error('❌ Exception handling magic link:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Check if user is from patient invitation
   */
  static async isPatientInvite(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('invitation_source, is_patient')
        .eq('id', userId)
        .single();

      if (error) {
        safeLog.error('❌ Error checking patient invite status:', error);
        return false;
      }

      return data?.invitation_source === 'management_panel' && data?.is_patient === true;
    } catch (error) {
      safeLog.error('❌ Exception checking patient invite:', error);
      return false;
    }
  }

  /**
   * Check if user needs to set password
   */
  static async needsPasswordSetup(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('invitation_source, password_set_at')
        .eq('id', userId)
        .single();

      if (error) {
        safeLog.error('❌ Error checking password setup status:', error);
        return false;
      }

      return data?.invitation_source === 'management_panel' && !data?.password_set_at;
    } catch (error) {
      safeLog.error('❌ Exception checking password setup:', error);
      return false;
    }
  }

  /**
   * Mark password as set
   */
  static async markPasswordSet(userId: string): Promise<{ error: any }> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          password_set_at: new Date().toISOString(),
          invitation_accepted_at: new Date().toISOString(),
          patient_status: 'active' // Set patient status to active after first login
        })
        .eq('id', userId);

      if (error) {
        safeLog.error('❌ Error marking password set:', error);
        return { error };
      }

      safeLog.info('✅ Password set timestamp and patient status updated');
      return { error: null };
    } catch (error) {
      safeLog.error('❌ Exception marking password set:', error);
      return { error };
    }
  }

  /**
   * Mark invitation as accepted (SECURE: uses authenticated user session)
   */
  static async markInvitationAccepted(userId: string): Promise<void> {
    try {
      // Get current authenticated user session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        safeLog.error('❌ No authenticated session for marking invitation accepted');
        return;
      }

      // Use authenticated client - RLS will allow user to update their own profile
      const { error } = await supabase
        .from('profiles')
        .update({
          invitation_accepted_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        safeLog.error('❌ Error marking invitation accepted:', error);
      } else {
        safeLog.info('✅ Invitation marked as accepted');
      }
    } catch (error) {
      safeLog.error('❌ Exception marking invitation accepted:', error);
    }
  }

  /**
   * Get patient profile data (SECURE: uses authenticated user session)
   */
  static async getPatientProfile(userId: string) {
    try {
      // Get current authenticated user session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        safeLog.error('❌ No authenticated session for fetching patient profile');
        return { data: null, error: { message: 'No authenticated session' } };
      }

      // Use authenticated client - RLS will allow user to access their own profile
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        safeLog.error('❌ Error fetching patient profile:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      safeLog.error('❌ Exception fetching patient profile:', error);
      return { data: null, error };
    }
  }

  /**
   * Validate password requirements
   */
  static validatePassword(password: string): {
    isValid: boolean;
    errors: string[];
    checks: {
      minLength: boolean;
      hasUppercase: boolean;
      hasLowercase: boolean;
      hasNumber: boolean;
    }
  } {
    const checks = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password)
    };

    const errors: string[] = [];

    if (!checks.minLength) errors.push('Mínimo 8 caracteres');
    if (!checks.hasUppercase) errors.push('Uma letra maiúscula');
    if (!checks.hasLowercase) errors.push('Uma letra minúscula');
    if (!checks.hasNumber) errors.push('Um número');

    return {
      isValid: Object.values(checks).every(check => check),
      errors,
      checks
    };
  }
}